<template>
  <div
    class="default-table shadow-none border-none table-compact position-relative"
  >
    <b-table-simple
      v-if="!versioningDisabled"
      responsive
      class="loaded"
      table-class="mt-0"
    >
      <b-thead>
        <b-tr>
          <b-th class="dir-table-primary-col">
            Key
          </b-th>
          <b-th class="dir-table-default-col">
            Last Modified
          </b-th>
          <b-th class="dir-table-default-col">
            Size
          </b-th>
          <b-th class="file-actions" />
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="version in versions"
          :key="version.versionId"
        >
          <b-td class="dir-table-primary-col">
            <div class="d-flex align-items-center">
              <span>
                <GjIcon
                  name="File"
                  size="20px"
                />
              </span>
              <div
                class="ml-1 d-flex flex-column flex-md-row align-items-start align-items-md-center"
                :class="
                  !version.isDeleteMarker && !version.isLatest
                    ? 'text-primary cursor-pointer'
                    : 'text-main'
                "
                @click="viewVersion(version)"
              >
                <span class="text-truncate mr-1">
                  {{ formatObjectName(version.key) }}
                </span>
                <span
                  v-if="version.isDeleteMarker"
                  class="text-sm text-danger"
                >
                  (Deleted)
                </span>
                <span
                  v-else-if="version.isLatest"
                  class="text-sm text-primary"
                >
                  (Current version)
                </span>
              </div>
            </div>
          </b-td>
          <b-td class="dir-table-default-col">
            {{ formatOffsetDateTimeDetailed(version.lastModified) }}
          </b-td>
          <b-td class="dir-table-default-col">
            {{ formatBytes(version.size) }}
          </b-td>
          <b-td class="file-actions py-0">
            <b-dropdown
              v-if="!version.isDeleteMarker"
              id="directory-field-actions-dropdown"
              dropleft
              variant="transparent"
              :disabled="deletingVersion"
              no-caret
            >
              <template #button-content>
                <GjIcon
                  name="Moreoptions"
                  class="m-0"
                />
              </template>
              <template #default>
                <b-dropdown-item
                  class="directory-field-actions-item"
                  @click="downloadVersion(version.versionId)"
                >
                  <GjIcon name="Download" />
                  Download
                </b-dropdown-item>
                <b-dropdown-item
                  class="directory-field-actions-item text-danger"
                  @click="
                    deleteVersion({
                      key: version.key,
                      versionId: version.versionId,
                    })
                  "
                >
                  <span class="text-danger">
                    <GjIcon name="Delete" />
                    Delete
                  </span>
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
    <div
      v-else
      class="py-4 my-2 text-center d-flex flex-column align-items-center"
    >
      <p>
        Versioning is not enabled in this bucket. You can enable versioning from
        bucket settings.
      </p>
      <b-button
        variant="bg-primary"
        class="m-0"
        :to="{
          name: 'bucket-settings',
          params: {
            projectId: $route.params.projectId,
            id: $route.params.id,
          },
        }"
      >
        <GjIcon
          class="icon-left"
          name="Settings"
        />
        Bucket Settings
      </b-button>
    </div>
    <table-busy v-if="gettingObjectVersioning" />
    <b-modal
      v-model="viewVersionModal"
      :title="`Version: ${viewingVersionId}`"
      ok-variant="outline-primary"
      ok-title="Close"
      body-class="p-0 position-relative"
      ok-only
      hide-footer
      size="lg"
      @hidden="
        () => {
          versionProperties = null
          gettingObjectVersionProperties = false
          viewingVersionId = null
        }
      "
      @ok.prevent="() => {}"
    >
      <file-view-version
        :version-data="versionProperties"
        :loading="gettingObjectVersionProperties"
        :viewing-version-id="viewingVersionId"
      />
    </b-modal>
  </div>
</template>

<script>
import helpers from '@/mixins/helpers'
import storageHelpers from '@/mixins/storage-helpers'

import FileViewVersion from './FileViewVersion/FileViewVersion.vue'

export default {
  name: 'FileVersions',
  components: {
    FileViewVersion,
  },
  mixins: [helpers, storageHelpers],
  props: {
    file: {
      type: Object,
      default: null,
      required: true,
    },
    accountId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      versions: [],
      versioningDisabled: false,
      gettingObjectVersioning: false,
      deletingVersion: false,
      gettingObjectVersionProperties: false,
      versionProperties: null,
      viewVersionModal: false,
      viewingVersionId: null,
    }
  },
  created() {
    this.getObjectVersioning()
  },
  methods: {
    viewVersion(version) {
      if (!version.isLatest && !version.isDeleteMarker) {
        this.viewVersionModal = true
        this.gettingObjectVersionProperties = true
        this.viewingVersionId = version.versionId
        const query = {
          accountId: this.accountId,
          ...this.file,
          bucketName: this.$route.params.id,
          versionId: version.versionId,
        }

        this.$store
          .dispatch('accountObjects/fetchObjectInfo', query)
          .then(res => {
            this.versioningDisabled = false
            this.gettingObjectVersionProperties = false
            this.versionProperties = res.data.result
          })
          .catch(() => {
            this.versionModal = false
            this.errorToast('Error viewing version')
          })
          .finally(() => {
            this.gettingObjectVersionProperties = false
          })
      }
    },
    getObjectVersioning() {
      this.gettingObjectVersioning = true
      const query = {
        accountId: this.accountId,
        ...this.file,
      }

      this.$store
        .dispatch('accountObjects/getObjectVersioning', query)
        .then(res => {
          this.versions = res.data.result
        })
        .catch(err => {
          if (err.response.status === 404) {
            this.versioningDisabled = true
          }
        })
        .finally(() => {
          this.gettingObjectVersioning = false
        })
    },
    downloadVersion(versionId) {
      this.$store
        .dispatch('accountObjects/generateQuickShareUrl', {
          accountId: this.accountId,
          query: {
            key: this.file.key,
            bucketName: this.file.bucketName,
            versionId,
          },
        })
        .then(res => {
          this.download(res.data.result)
        })
        .catch(() => {
          this.errorToast('Cannot download file')
        })
    },
    download(url) {
      const link = document.createElement('a')
      link.download = this.formatObjectName(this.file.key)
      link.href = url
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    deleteVersion(version) {
      this.confirmDelete(
        'Delete file version',
        'Are you sure you want to delete this version?',
      ).then(ok => {
        if (ok) {
          this.deletingVersion = true
          const query = {
            accountId: this.accountId,
            bucketName: this.$route.params.id,
            ...version,
          }

          this.$store
            .dispatch('accountObjects/deleteObjectVersion', query)
            .then(() => {
              this.successToast('Object version deleted successfully')
              this.getObjectVersioning()
            })
            .catch(() => {
              this.errorToast("Couldn't delete version")
            })
            .finally(() => {
              this.deletingVersion = false
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.directory-field-actions-item {
  width: 170px;
}
</style>
